import { alpha } from "@mui/material/styles";
import type {
  ColorRange,
  PaletteColor,
} from "@mui/material/styles/createPalette";

const withAlphas = (color: PaletteColor): PaletteColor => {
  return {
    ...color,
    alpha4: alpha(color.main, 0.04),
    alpha8: alpha(color.main, 0.08),
    alpha12: alpha(color.main, 0.12),
    alpha30: alpha(color.main, 0.3),
    alpha50: alpha(color.main, 0.5),
  };
};

// Plain white. Every group below still spells out its own "#FFFFFF" for
// clarity in context (card surfaces, button labels, knob fills, …) — this is
// the one generic export for call sites that don't belong to any of those
// groups and would otherwise re-declare the literal.
export const white = "#FFFFFF";

export const neutral: ColorRange = {
  50: "#F9FAFB",
  100: "#F3F4F6",
  200: "#E5E7EB",
  300: "#D2D6DB",
  400: "#9DA4AE",
  500: "#6C737F",
  600: "#4D5761",
  700: "#374151",
  800: "#1F2937",
  900: "#111827",
};

export const inkfree = withAlphas({
  lightest: "#ECECEC", // Very pale tint of brand
  light: "#FCECE7", // Lighter tint of brandF6F6F6
  main: "#dd3824", // Brand color
  // secondary: "#00A1FF",
  dark: "#A8320C", // Shade for hover / active states
  darkest: "#6E1F05", // Deepest shade
  contrastText: "#FFFFFF", // White for contrast
});

// App-wide keyboard focus-visible ring. Every `:focus-visible` outline in the
// app (cards, rows, custom controls that don't get MUI's default focus
// styling for free) uses the brand colour, matching the ring the global
// `MuiButton` theme draws — components should always draw it as
// `outline: "2px solid ${focusRing}"` rather than repeating the value.
export const focusRing = inkfree.main;

// ---------------------------------------------------------------------------
// Primary action button tokens — the chrome every `variant="contained"` button
// wears (height, padding, radius, type scale and the disabled state). Applied
// globally through the `MuiButton` `contained` override in
// `theme/light/create-components.ts`, so no screen needs to restyle it.
// The fill deliberately tracks the app brand colour (`inkfree`) rather than the
// design mock's orange, so a brand change re-skins every button at once.
// ---------------------------------------------------------------------------
export const button = {
  // Contained (primary) fill. Deliberately tracks the app brand colour
  // (`inkfree`) rather than the design mock's orange, so a brand change
  // re-skins every button at once. The re-skinned screens that need the orange
  // accent still override the fill locally; everything else in this object is
  // the shared chrome those overrides no longer have to repeat.
  bg: inkfree.main,
  // No `hoverBg`: a contained button holds `bg` on hover and only shifts on
  // press. See the `contained` override in `theme/light/create-components.ts`.
  activeBg: inkfree.darkest,
  color: inkfree.contrastText,
  height: 40,
  paddingX: "20px",
  radius: "9px",
  fontSize: "13px",
  fontWeight: 700,
  // Gap between a button's leading/trailing icon and its label.
  iconGap: "8px",
  // Shared transition for every interactive state.
  transition:
    "background-color .15s ease, border-color .15s ease, color .15s ease, box-shadow .15s ease",
  // Keyboard focus ring — brand-coloured, offset off the button edge.
  focusRing: inkfree.main,
  // Design's disabled primary button: flat lilac fill, muted label.
  disabledBg: "#F4F3F8",
  disabledColor: "#B7B2C4",
  // Secondary / outlined action (Cancel, Manage seats, Cancel subscription):
  // white fill, neutral hairline, muted ink label that darkens to brand ink on
  // hover. One shared treatment for every "outlined" button.
  secondaryBg: "#FFFFFF",
  secondaryColor: "#4A4462",
  secondaryBorder: "#E3E1EC",
  secondaryHoverBorder: "#211547",
  secondaryHoverColor: "#211547",
  // Low-emphasis text button.
  textColor: "#4A4462",
  textHoverBg: "rgba(33,21,71,0.04)",
  // A second, warmer outlined/ghost border shade a handful of screens
  // standardised on — distinct from `secondaryBorder` above, not a typo.
  secondaryBorderWarm: "#E3DFD4",
};

// Size steps for the global `MuiButton` size overrides. `medium` is the default
// and mirrors `button` above; `small` is the compact action used in dense rows
// (inbox rows, table rows, card headers) and `large` the prominent page CTA.
// Vertical padding is always 0 — the height alone sets how tall a button reads,
// so trim `height` here rather than adding padding back.
export const buttonSize = {
  small: {
    height: 32,
    paddingX: "14px",
    radius: "8px",
    fontSize: "12.5px",
  },
  medium: {
    height: button.height,
    paddingX: button.paddingX,
    radius: button.radius,
    fontSize: button.fontSize,
  },
  large: {
    height: 44,
    paddingX: "24px",
    radius: "10px",
    fontSize: "14px",
  },
};

// Signature brand gradient — orange → coral. Use for headers, selected
// states, and progress fills that need the full brand identity, not just
// a solid colour. Update this single constant to reshape the app.
export const brandGradient =
  "linear-gradient(92.19deg, #F1692C 1.95%, #EB5060 97.4%)";

// Default color for titles/headings and body text. Replaces literal black
// (`#000`, `#000000`, `"black"`) throughout the app. Wired into
// `palette.text.primary` so any Typography without an explicit `color`
// picks it up automatically.
export const headingText = "#1A1A1A";

// Default color for subtext / secondary text. Replaces ad-hoc grays used
// for helper text, captions, meta info, etc. Wired into
// `palette.text.secondary` so any Typography with `color="text.secondary"`
// (or MUI's built-in `textSecondary`) picks it up automatically.
export const subText = "#646464";

// Default color for disabled text / controls. Wired into
// `palette.text.disabled` so any Typography with `color="text.disabled"`
// (or MUI's disabled state) picks it up automatically.
export const disabledText = "#C3C3C3";

// Default color for input placeholder text (ghost text in empty fields).
// Wired into `palette.text.placeholder` so input components can pull it
// through the theme instead of hardcoding the hex.
export const placeholderText = "#767676";

// Gold accent used for upgrade / premium prompts (sparkle icons, "unlock
// this feature" callouts). Wired into `palette.premium` so any component
// signalling a paid tier pulls the same shade.
export const premiumGold = "#FFD700";

// ---------------------------------------------------------------------------
// Reporting & Insights chart tokens. Every colour used by the Insights line,
// funnel and donut charts (and the surrounding report cards) lives here so the
// charts never carry a raw hex. Access via theme.palette.chart.* — change the
// whole reporting look from this one block.
// ---------------------------------------------------------------------------
// Series and status hues are the plot's only carrier of "which line/slice is
// which", so WCAG 1.4.11 asks 3:1 against the `canvas` they sit on; the axis is
// text, so it needs 4.5:1 (WCAG 1.4.3). Where a value fell short it was
// darkened on the same hue — the palette reads unchanged, just deeper.
export const chart = {
  // Workflow-trend / funnel series.
  series: {
    requested: "#868D99", // grey (was #9CA3AF, 2.43:1)
    sent: "#3E7DF2", // blue
    opened: "#7C5CD6", // purple
    completed: "#1E9E5A", // green
  },
  // Request-status distribution (donut) palette, keyed by status.
  status: {
    pending: "#3E7DF2",
    signed: "#1E9E5A",
    scheduled: "#7C5CD6",
    draft: "#0FA189", // was #12B39A, 2.53:1
    declined: "#E5484D",
    rejected: "#EC4899",
    inReview: "#4C63D2",
    voided: "#8A85A0",
    expired: "#CB7A18", // was #E8912B, 2.35:1
    fallback: "#868D99", // any unmapped status (was #9CA3AF, 2.43:1)
  },
  // Chart chrome / neutrals.
  grid: "#E7E4DE", // dashed grid lines
  axis: "#736D8D", // axis tick text (was #9B96AE, 2.73:1)
  cursor: "#D1D5DB", // hover cursor / conversion arrows
  track: "#EDEAE2", // empty donut ring
  canvas: "#FAFAF7", // inner chart panel background
  panelBorder: "#EFEDE6", // inner chart panel border
  cardBorder: "#EDEAF3", // outer report card border
  label: "#211547", // in-chart numeric labels
  cardShadow: "0 1px 3px rgba(33,21,71,0.05)",
  tooltipShadow: "0px 4px 12px rgba(33,21,71,0.12)",
};

export const success = withAlphas({
  lightest: "#F0FDF9",
  light: "#3FC79A",
  main: "#10B981",
  dark: "#0B815A",
  darkest: "#134E48",
  contrastText: "#FFFFFF",
});

export const info = withAlphas({
  lightest: "#ECFDFF",
  light: "#CFF9FE",
  main: "#06AED4",
  dark: "#0E7090",
  darkest: "#164C63",
  contrastText: "#FFFFFF",
});

export const warning = withAlphas({
  lightest: "#FFFAEB",
  light: "#FEF0C7",
  main: "#F79009",
  dark: "#B54708",
  darkest: "#7A2E0E",
  contrastText: "#FFFFFF",
});

export const error = withAlphas({
  lightest: "#FEF3F2",
  light: "#FEE4E2",
  main: "#F04438",
  dark: "#B42318",
  darkest: "#7A271A",
  contrastText: "#FFFFFF",
});

// InkFree mobile design-system tokens (from Figma "InkFree Mobile Application").
// Keep the exact Figma values here so any future color change happens in ONE place.
// Access in components via theme.palette.mobile.<group>[<shade>].
export const mobile = {
  // Primary accent — app brand red (`inkfree.main` = #dd3824). Solid fills only;
  // the 50 tint stays the light peach wash.
  ePurple: {
    50: "#FDE8DF",
    500: "#dd3824",
    700: "#A8320C",
  },
  // Light "primary gradient" stops used for stepper circles / template row.
  peach: {
    100: "#FDE8DF",
    200: "#FBDCDF",
  },
  // Primary gradient stops: orange (#F1692C, also the dropzone border) → coral.
  orange: {
    500: "#F1692C",
  },
  coral: {
    500: "#EB5060",
  },
  base: {
    50: "#E9E8ED",
    100: "#BAB6C6",
    200: "#9187B4",
    300: "#6A6284",
    500: "#211547",
    900: "#0E091E",
  },
  grey: {
    50: "#DFDFDF",
    100: "#C3C3C3",
    200: "#A6A6A6",
    400: "#7D7D7D",
    700: "#A8A8A8",
    900: "#636363",
  },
  surface: {
    white: "#FFFFFF",
    100: "#F0F0F0",
  },
  red: {
    50: "#FFEFEF",
    400: "#E78A85",
    500: "#CE150A",
    700: "#BC2C23",
  },
  blue: {
    50: "#E6F6FF",
    500: "#00A1FF",
  },
  // Uploaded-document card gradient stops (lavender → sky → peach).
  docCard: {
    lavender: "#F2E6FF",
    sky: "#E2EBF9",
    peach: "#FFDDCE",
  },
  green: {
    500: "#00CC77",
  },
  yellow: {
    100: "#FFEFB0",
    800: "#8C7000",
  },
};

// Uploaded-document card fill (lavender → sky → peach). Shared by the mobile
// upload step and the desktop file chip so the look changes in ONE place.
export const documentCardGradient = `linear-gradient(99deg, ${mobile.docCard.lavender} 0.87%, ${mobile.docCard.sky} 50.11%, ${mobile.docCard.peach} 99.35%)`;

// ---------------------------------------------------------------------------
// External "Signer Experience" redesign tokens.
// The guest signing flow (invite landing, signing view, decline, completion,
// signature & delegate modals) uses a purple + orange system. Every design
// hex lives here so components reference `theme.palette.signer.*` tokens only
// — never a raw hex. Change the look for the whole flow from this one block.
// Access in components via theme.palette.signer.<group>.<key>.
// ---------------------------------------------------------------------------
export const signer = {
  // Deep brand ink — headings, primary text, dark surfaces (exact = base 500).
  ink: mobile.base[500], // #211547
  signatureInk: "#1B1B4B", // adopted-signature stroke color
  docTitle: "#3D5A8F", // serif document title (blue)
  docText: "#2B2B33", // serif document body text
  // Orange — primary signer action (Review & sign, Start signing, Sign here).
  // `main`/`dark` were the design mock's orange (#E1500F/#C74409); both now
  // track the app brand red (`inkfree.main`/`.dark`) instead, so every border,
  // focus ring, icon stroke, text colour and gradient stop that reads this
  // group is brand red, not orange. The lighter tint/wash shades below
  // (`tint`/`faint`/`faintest`/`hover`/`border`/`progressTrack`) are distinct
  // hex values, not derived from `main`, and are unaffected by this change.
  orange: {
    main: inkfree.main,
    dark: inkfree.dark, // hover / active
    // White text on `main` now clears 4.5:1 WCAG 1.4.3 on its own (brand red,
    // not the old orange), so the small-label `dark`-as-resting-fill/`darker`
    // hover-step workaround this used to need no longer applies. `darker` has
    // no remaining consumers — kept only in case a future small-label control
    // needs the extra step.
    darker: "#A83A07",
    muted: "#B06A45", // helper text sitting on an orange field
    border: "#F0C9B8",
    tint: "#FDF1EC", // chip / active row background
    faint: "#FFF8F4", // input field background
    faintest: "#FFFBF9", // subtlest hover
    hover: "#FDEDE3", // "Sign here" hover fill
    progressTrack: "#F6DACB", // progress-bar track
  },
  // Purple — secondary action (Delegate) and AI accents.
  purple: {
    main: "#6D52A2",
    dark: "#54417E", // hover
    light: "#9B7FD4", // gradient stop
    border: "#E3D6F5",
    borderStrong: "#D8CBEE",
    tint: "#F4EDFB",
    faint: "#FAF7FE",
    // A second, slightly cooler purple tint used specifically for accent
    // chip/tile backgrounds (revoked API key, folder icon tile, admin chip)
    // — genuinely distinct from `tint` above, not interchangeable with it.
    chipBg: "#F0ECF8",
  },
  // Green — completed / success states.
  success: {
    // WCAG 1.4.3: #1F8A4C was 4.05:1 under small bold text on `tint` (the
    // signer's "Secure request" badge); #177C46 is 4.9:1.
    main: "#177C46",
    border: "#CFE7D6",
    tint: "#F1F8F3",
    faint: "#FBFDFB",
  },
  // Red — decline / destructive.
  danger: {
    main: "#B4372F",
    dark: "#8F2B25",
    border: "#F2D2CF",
    borderStrong: "#EBC5C2",
    tint: "#FDF3F2",
  },
  // Amber — due-date / expiry callout.
  warn: {
    main: "#B47207",
    text: "#8F5B06",
    border: "#F6E7C9",
    tint: "#FDF6EC",
  },
  // Text neutrals — carry the design's slight purple tint so body copy sits in
  // the same family as `ink`, rather than reading as flat grey against it.
  text: {
    secondary: "#4A4462", // quote copy, "Preview" button label
    meta: "#6B6584", // sender email, page counts, document meta
    muted: "#6B6584", // card footer legal line, request ID
    placeholder: "#6B6584", // input / textarea placeholder
    // Lighter than `meta` — labels, sort/sub text, icon glyphs. Previously
    // re-declared under three component-scoped names with the same value
    // (`formField.disabledText`, `panel.metaText`, `profileMenu.sectionLabel`,
    // all once #9B96AE); this is the generic home for any other consumer.
    // WCAG 2.1 AA 1.4.3: the design's #9B96AE is 2.85:1 on white, so it is
    // darkened to #6E6886: 5.27:1 on white and at least 4.65:1 on every tinted
    // surface it sits on (#F4F6FD field fill, #F1F0F5, #F3F1EC, #FDF1EC).
    faint: "#6E6886",
    // Darker than `faint`, lighter than `meta` — page/card subtitle and body
    // description copy (e.g. "Manage your team's contacts"). Previously only
    // existed as `chart.status.voided`'s incidental value; that name is
    // report-specific and wasn't a fit for the ~35 unrelated call sites
    // already using this exact grey for subtitle/description text.
    // WCAG 2.1 AA 1.4.3: the design's #8A85A0 is 3.53:1 on white; #6F6987 is
    // 5.19:1 on white and 4.8:1 on the lavender fills.
    subtitle: "#6F6987",
  },
  // Surfaces & light-neutral borders. The three fills carry the design's
  // lavender tint — it is what separates a section from the white card behind
  // it, so flattening them to white erases the card's internal structure.
  surface: {
    canvas: "#F6F4FA", // signing-view page background
    subtle: "#FAF9FC", // section fills — quote block, list header, card footer
    muted: "#F4F2F9", // hover / track / PDF thumbnail stripe
    // Neutral count/number pill fill (row counters, level badges). Previously
    // only existed as `panel.countPillBg`, a name scoped to the inbox/
    // notification popovers — this is the generic home for the ~6 other call
    // sites already using this exact grey for the same pill shape.
    pill: "#F1F0F5",
    // Row/section hover wash (list rows, card body hover). Distinct from
    // `subtle`/`muted` above — six screens had each landed on this near-
    // identical warm-white independently.
    hover: "#FBFAF7",
  },
  border: {
    light: "#EFEDF5",
    main: "#E9E5F2",
    strong: "#C9C5D6",
    // Row/list hairline divider. A distinct (if close) shade from `light` —
    // previously only existed as `table.divider`, a name scoped to
    // `CustomTable`; this is the generic home for the many non-table rows
    // already using this exact grey as their divider.
    divider: "#F2F0F7",
  },
  disabled: "#D5D2E0", // disabled button fill / dashed field border
  // Accent-bar + completion-icon gradient stops (purple → coral → amber).
  gradient: {
    coral: "#EF403A",
    amber: "#FBB042",
  },
  // Clause-highlight annotation colors (decline flow). Each swatch carries its
  // chip background, chip text, and check-mark shade.
  highlight: {
    yellow: {
      main: "#FBBF24",
      chipBg: "#FEF3C7",
      chipText: "#92600A",
      check: "#7A4E00",
    },
    orange: {
      main: "#FB923C",
      chipBg: "#FFEDD5",
      chipText: "#9A3412",
      check: "#7C2D12",
    },
    red: {
      main: "#F87171",
      chipBg: "#FEE2E2",
      chipText: "#991B1B",
      check: "#7F1D1D",
    },
    blue: {
      main: "#60A5FA",
      chipBg: "#DBEAFE",
      chipText: "#1E40AF",
      check: "#1E3A8A",
    },
  },
};

// ---------------------------------------------------------------------------
// Toggle-switch tokens — the pill control the redesigned Settings screens
// standardised on (38×22 track, 16px knob, 3px inset). Wired into the global
// `MuiSwitch` override in `theme/light/create-components.ts`, so every
// `<Switch>` in the product inherits it and no screen restyles a switch of its
// own. Components reference `theme.palette.switchControl.*`, never a raw hex.
// ---------------------------------------------------------------------------

/** Resting (off) track. Declared separately so the disabled tint derives from it. */
const switchTrackOff = "#D9D6E4";

export const switchControl = {
  /* -- colours -- */
  /** Off. */
  trackOff: switchTrackOff,
  /** On — the app brand colour (`inkfree.main` = #dd3824). Solid track fill. */
  trackOn: inkfree.main,
  /** Knob fill, identical in every enabled state. */
  knob: "#FFFFFF",
  knobShadow: `0 1px 2px ${alpha("#000000", 0.2)}`,
  /**
   * Disabled keeps the same hues, drained — the control still reads as on or
   * off, just unavailable. 0.38 is MUI's disabled opacity.
   */
  trackOffDisabled: alpha(switchTrackOff, 0.5),
  trackOnDisabled: alpha(inkfree.main, 0.38),
  /** Keyboard focus ring, drawn as a halo around the track. Derives from the
   * brand fill so the halo matches the (now red) on-state track. */
  focusRing: alpha(inkfree.main, 0.24),
  focusRingWidth: "3px",

  /* -- geometry -- */
  width: 38,
  height: 22,
  knobSize: 16,
  /** Gap between knob and track on all four sides. */
  knobInset: 3,
  /** How far the knob slides when checked: width - knobSize - 2 * knobInset. */
  knobTravel: 16,
  trackRadius: "999px",
  /** Compact variant — the allowlist-row toggle in Security Policies. */
  small: {
    width: 34,
    height: 20,
    knobSize: 14,
    knobInset: 3,
    knobTravel: 14,
  },

  /* -- motion -- */
  trackTransition: "background-color .15s",
  knobTransition: "transform .15s",
};

// ---------------------------------------------------------------------------
// Split-button tokens — the two-part "Create Request" control that
// `CustomDropdownButton` renders: a filled main half welded to a one-shade-
// darker caret half, parted by a translucent hairline. The caret fill and the
// divider are what make it read as a split control rather than a plain button.
//
// Every re-skinned header (Sent, Templates, Inbox) had converged on exactly
// these colours by hand, while the callers that styled nothing (side nav,
// Dashboard) fell through to the flat brand fill and looked like a different
// component. The colours therefore live here and the component applies them by
// default; a screen may still override its own size, per
// UI_IMPLEMENTATION_RULES § Do Not Force Consistency.
// ---------------------------------------------------------------------------
export const splitButton = {
  // Solid fill tracks the app brand colour (`inkfree.main` = #dd3824), not the
  // design's orange — every `CustomDropdownButton` reads as the brand action.
  bg: inkfree.main,
  hoverBg: inkfree.dark,
  color: "#FFFFFF",
  radius: "10px",
  padding: "12px 18px",
  fontSize: "13.5px",
  fontWeight: 700,
  /** Icon ↔ label gap. The component zeroes MUI's `startIcon` margin so this is
   * the only spacing in play. */
  gap: "9px",
  iconSize: 16,

  /**
   * Caret half. It shares the main half's fill so the control reads as one
   * button, and the hairline is the only thing that separates them — which
   * makes the divider load-bearing rather than decorative. Hover matches the
   * main half too, so the two never disagree mid-interaction.
   */
  caretWidth: 42,
  caretBg: inkfree.main,
  caretHoverBg: inkfree.dark,
  caretDivider: alpha("#FFFFFF", 0.25),
  caretIconSize: 14,
};

// ---------------------------------------------------------------------------
// Content-card tokens — the white surface chrome (fill, hairline border,
// resting shadow) shared by every screen's primary content card/list wrapper
// (Contacts, Templates, Sign Requests, Inbox, Sign Request Detail). Radius is
// deliberately NOT included here — most screens use 16px but Sign
// Requests/Inbox use 14px for the same card, so that value stays local to
// each screen rather than being forced to match.
// ---------------------------------------------------------------------------
export const contentCard = {
  bg: "#FFFFFF",
  border: "#EDEAF3",
  shadow: "0 1px 3px rgba(33,21,71,.05)",
  // A second, distinct "white card" border shade the Settings screens
  // standardised on (`#E9E7F1`, not `border` above) — genuinely a different
  // value, not interchangeable with it, but reused identically across those
  // screens' own cards. Previously only existed as `table.border`, a name
  // scoped to `CustomTable`.
  borderMuted: "#E9E7F1",
};

// ---------------------------------------------------------------------------
// Table tokens — the chrome shared by every data table in the app, taken from
// the Plan & Billing lists (Payment Methods / Invoices) which the design treats
// as the canonical table. `CustomTable` is the only consumer, so changing a
// value here re-skins every table in the product at once. Components reference
// `theme.palette.table.*` and never a raw hex.
// ---------------------------------------------------------------------------
export const table = {
  /** Card surface the table sits on. */
  surface: "#FFFFFF",
  /** Card chrome — one step warmer than `signer.border.main`. */
  border: "#E9E7F1",
  /** Hairline between body rows. */
  divider: "#F2F0F7",
  shadow: `0 1px 3px ${alpha(signer.ink, 0.04)}`,

  /** Grey column-header strip. */
  header: {
    bg: signer.surface.subtle,
    border: signer.border.light,
    text: signer.text.muted,
    /** Label of the column currently sorted. */
    activeText: signer.ink,
  },

  row: {
    text: signer.ink,
    secondaryText: signer.text.meta,
    hover: signer.surface.subtle,
    selected: signer.surface.muted,
  },

  /** Title / description block above the column headers. */
  caption: {
    title: signer.ink,
    description: signer.text.meta,
  },

  /** Action-column glyphs and any icon rendered inside a cell. */
  icon: {
    rest: signer.text.muted,
    hover: signer.ink,
  },

  /** Selection controls (header checkbox, row checkbox / radio). */
  selection: {
    rest: "#D9D6E4",
    active: inkfree.main,
    countText: signer.text.meta,
  },

  /** No-rows placeholder. */
  empty: {
    text: signer.text.meta,
  },

  pagination: {
    border: signer.border.light,
    text: signer.text.meta,
    hover: signer.surface.muted,
    selectedBg: inkfree.main,
    selectedText: "#FFFFFF",
    ellipsis: signer.text.muted,
    disabled: "#D9D6E4",
  },
};

// ---------------------------------------------------------------------------
// Form-field tokens — the chrome shared by every redesigned input control
// (text field, dial-code select, …): resting fill and border, focus border,
// value / placeholder ink and the disabled state. Every field colour lives
// here so components reference `theme.palette.formField.*` only — never a raw
// hex — and the whole form system re-skins from this one block.
// ---------------------------------------------------------------------------
export const formField = {
  label: signer.ink, // field label ink
  labelMuted: "#6B6584", // read-only / non-editable field label
  required: "#C74409", // required asterisk
  bg: "#FAFAFD", // resting fill
  border: "#E3E1EC", // resting border
  focusBorder: signer.orange.main, // focused border
  text: signer.ink, // typed value
  placeholder: "#6B6584",
  adornment: "#6B6584", // chevrons / icons inside a field
  disabledBg: "#F4F3F8",
  disabledBorder: signer.border.light,
  disabledText: "#9B96AE",
  // A second, warmer placeholder shade the Settings/SignRequest screens
  // standardised on — genuinely distinct from `placeholder` above, not a
  // typo of it.
  placeholderMuted: "#A7A3B8",
  // A second, warmer field/toolbar border shade (search fields, pagination
  // buttons, toolbar rules) — distinct from `border` above.
  borderWarm: "#E9E5DA",
};

// ---------------------------------------------------------------------------
// Dropdown-menu tokens — every floating action menu opened from a kebab / more
// button (request rows, template rows, header menus) and every select popup.
// One block so the whole app shares the same menu card, item rhythm and
// destructive treatment; wired into the global `MuiMenu` / `MuiMenuItem`
// overrides so components never restyle a menu themselves.
// ---------------------------------------------------------------------------
export const menu = {
  border: "#EAE7F2",
  radius: "12px",
  shadow: `0 12px 34px ${alpha(signer.ink, 0.18)}`,
  /** Padding around the item list. */
  padding: "6px",
  /** Minimum width of an action menu (not applied to select popups). */
  minWidth: 190,
  itemRadius: "8px",
  itemPadding: "9px 11px",
  itemGap: "10px",
  itemFontSize: "13px",
  itemFontWeight: 600,
  itemText: "#4A4462",
  itemHoverBg: "#F4F2F9",
  itemHoverText: signer.ink,
  itemSelectedBg: signer.orange.tint,
  itemSelectedText: signer.orange.main,
  /** Destructive rows — "Void request", "Delete". */
  dangerText: "#C0392B",
  dangerHoverBg: "#FDECEB",
};

// ---------------------------------------------------------------------------
// Avatar / profile dropdown tokens, per the Inkfree Dashboard reference: a
// text-only menu split into a personal group and an admin-only Organization
// group, closing with a red sign-out row.
// ---------------------------------------------------------------------------
export const profileMenu = {
  itemText: "#4A4462",
  itemHoverBg: "#F4F2F9",
  itemHoverText: signer.ink,
  divider: "#F4F2F9",
  sectionLabel: "#6E6886", // "ORGANIZATION" heading + email line (WCAG 1.4.3, was #9B96AE at 2.85:1)
  orgName: "#6B6584",
  adminChipBg: "#F0EBF8",
  adminChipText: signer.purple.main,
  signOut: signer.gradient.coral,
  signOutHoverBg: "#FDECEB",
  border: "#EDEAF3",
  radius: "18px",
};

// ---------------------------------------------------------------------------
// Top-nav shell tokens — the icon buttons (inbox, notifications, help), the
// rules between groups, and the unread badge. One block so the bar stays in
// step with the side nav and the popovers it opens.
// ---------------------------------------------------------------------------
export const topNav = {
  icon: "#6B6584", // resting glyph
  iconHoverBg: "#F4F2F9", // 38px button hover fill
  divider: signer.border.light, // 1px rules either side of the icon group
  badge: inkfree.main, // count pill / unread dot
};

// ---------------------------------------------------------------------------
// Floating panel tokens — the top-nav Inbox and Notifications popovers. Both
// are the same surface (header + rows + footer strip), so they share one block
// and stay in step with each other.
// ---------------------------------------------------------------------------
export const panel = {
  shadow: `0 8px 24px ${alpha(signer.ink, 0.1)}`,
  title: signer.ink, // "Inbox" / "Notifications" and row titles
  bodyText: "#6B6584", // row description lines
  metaText: "#6E6886", // timestamps, "Name · 3 months ago" (WCAG 1.4.3, was #9B96AE at 2.85:1)
  stripe: signer.orange.faintest, // alternating row fill + footer strip
  hover: signer.orange.faint,
  border: signer.border.light,
  countPillBg: "#F1F0F5", // neutral "3 new" pill
  countPillText: "#6B6584",
  accentPillBg: signer.orange.tint, // brand "2 awaiting you" pill
  accentPillText: signer.orange.main,
  unreadDot: inkfree.main,
  skeleton: "#ECE9F3", // loading placeholder fill — reads on white and stripe
};

// ---------------------------------------------------------------------------
// Navigation shell tokens. The desktop drawer (SideNav) and the mobile drawer
// (MobileNav) both pull every colour from here so the two stay in sync — change
// the whole nav look from this one block. Components never hardcode these;
// they read them through the `--nav-*` CSS variables each drawer declares.
// ---------------------------------------------------------------------------
export const nav = {
  itemText: "#4A4462", // resting item label + icon
  hoverBg: "#F4F2F9", // row hover fill
  activeBg: signer.orange.tint, // active row tint
  // Active label stays ink black (not the brand orange) — the tinted row and
  // the orange icon already carry the selected state.
  activeText: headingText,
  activeIcon: signer.orange.main,
  // 3px marker on the rail beside an active sub-level row. Sub-rows wear the
  // same tint + ink label as a top-level active row, so this bar is the only
  // thing distinguishing the two depths.
  activeBar: signer.orange.main,
  footerText: "#6B6584", // "Close Sidebar" row
  border: signer.border.light,
};

// Signer accent bar (purple → coral → amber). The 5px ribbon atop invite,
// completion, and modal cards.
export const signerAccentBar = `linear-gradient(90deg, ${signer.purple.main}, ${signer.gradient.coral}, ${signer.gradient.amber})`;

// Page backdrop behind the signer cards: deep brand ink with a purple glow off
// the top-right and a faint coral glow off the bottom-left. `color` is the
// base fill, `image` the two radial washes layered over it.
export const signerBackdrop = {
  color: signer.ink,
  image: [
    `radial-gradient(90% 70% at 80% -10%, ${alpha(signer.purple.main, 0.55)}, transparent 60%)`,
    `radial-gradient(70% 55% at 5% 105%, ${alpha(signer.gradient.coral, 0.22)}, transparent 55%)`,
  ].join(", "),
};

// Completion-icon gradients, keyed by terminal outcome.
export const signerDoneGradient = {
  signed: `linear-gradient(135deg, ${signer.purple.main}, ${signer.gradient.coral}, ${signer.gradient.amber})`,
  delegated: `linear-gradient(135deg, ${signer.purple.main}, ${signer.purple.light})`,
  declined: `linear-gradient(135deg, ${signer.danger.main}, ${signer.gradient.coral})`,
};

// Glow cast by the completion icon — the same hue as its gradient, so the halo
// reads as light off the badge rather than a generic drop shadow.
export const signerDoneShadow = {
  signed: `0 10px 26px ${alpha(signer.gradient.coral, 0.3)}`,
  delegated: `0 10px 26px ${alpha(signer.purple.main, 0.3)}`,
  declined: `0 10px 26px ${alpha(signer.danger.main, 0.3)}`,
};

// ---------------------------------------------------------------------------
// Toast / notification tokens — "Tinted fill" (design option 3A). The whole
// card takes a soft pastel fill of the status hue with darker same-hue text,
// so the status reads at a glance even without the icon. One entry per app
// variant (success / info / warning / error); the info shade reuses the
// design's neutral card and warning reuses its amber banner. Every design hex
// lives here so components reference `theme.palette.notification.*` only —
// never a raw hex. Access via theme.palette.notification.<variant>.<role>.
// ---------------------------------------------------------------------------
export const notification = {
  success: {
    bg: "#EAF7F0", // soft green fill
    border: "#CDEBDA",
    // Also read as text (status chips, "Active", "+100"): #1E9E5A was 3.44:1 on
    // white and 3.12:1 on `bg`, so it takes `title`'s shade (5.24 / 4.76:1).
    icon: "#177C46",
    title: "#177C46",
    text: "#2E6B4A",
  },
  info: {
    bg: "#F1F0F5", // neutral purple-grey fill
    border: "#E1DEEA",
    icon: "#6B6584",
    title: "#4A4462",
    text: "#6B6584",
  },
  warning: {
    bg: "#FDF3E7", // soft amber fill
    border: "#F0DFC2",
    // WCAG 1.4.3: #9A6B10 was 4.27:1 on `bg` under small text; `text`'s shade
    // is 5.19:1.
    icon: "#8A5E0E",
    title: "#8A5E0E",
    text: "#8A5E0E",
  },
  error: {
    bg: "#FDECEC", // soft red fill
    border: "#F2CFCF",
    // Also read as text by ~20 danger buttons and labels, so it must clear
    // WCAG 1.4.3: #D64545 was 4.38:1 on white and 3.83:1 on `bg`; #C23838 is
    // 5.36:1 and 4.69:1.
    icon: "#C23838",
    title: "#B23333",
    text: "#A83232",
  },
  // Elevation shared by all toast cards — deep-purple ink at 10% (design 3A).
  shadow: `0 8px 24px ${alpha(signer.ink, 0.1)}`,
};

// ---------------------------------------------------------------------------
// Status chip tokens — the dot + pill that labels a record's state (inbox rows
// and any other list needing an at-a-glance status). Tones reuse tokens the app
// already owns, so a "success" chip and a success toast are the same green and
// `action` is the same orange the signer flow uses for "your move" moments.
// Consumed through `CustomStatusChip`; components never restyle it.
// ---------------------------------------------------------------------------
export const statusChip = {
  dotSize: 6,
  gap: "7px",
  padding: "5px 11px",
  radius: "999px",
  fontSize: "12px",
  fontWeight: 700,
  tone: {
    /** Needs the user to act ("Your turn to sign"). */
    action: { bg: signer.orange.tint, text: "#A83A07" },
    /** Finished successfully ("Signed"). */
    success: { bg: notification.success.bg, text: "#177C46" },
    /** In flight, nothing for the user to do ("Waiting for others"). */
    info: { bg: "#EAF1FE", text: "#2563C4" },
    /** Terminal negative outcome ("Declined"). */
    danger: { bg: notification.error.bg, text: "#A83232" },
    /** Anything else — voided, expired, unmapped statuses. */
    neutral: { bg: notification.info.bg, text: notification.info.title },
  },
};
